import { useMemo } from 'react'
import { ChevronRight } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useAppSelector } from '@/store/hooks'
import { deriveNavGroups, findActiveItem } from '@/components/sidebar/nav-model'
import { useLocale, pickI18n } from '@/hooks/useLocale'

// 路由 → 板块标题映射(静态回退)。
// 顶栏标题优先从后端 role→menu(labelI18n)派生,与左侧菜单单一真相源、随语言切换;
// 仅当当前路由不在菜单里(如 /profile /settings,或菜单尚未加载)时才回退到这张表。
// 注意: 回退用 startsWith() 匹配，所以更具体的前缀必须排在前面
// (e.g. '/asset-mgr/inventory-transactions' 要在 '/asset-mgr/inventory' 之前)。
const routeTitles: Record<string, string> = {
  '/dashboard': 'Dashboard - Customer Service',
  '/inbox': 'Inbox',
  '/messages': 'Messages',
  '/reservations': 'Reservations',
  '/reviews': 'Reviews',
  '/guests': 'Guests',
  '/tasks': 'Tasks',
  '/resource-map-view': 'Resource Map View',
  '/listings': 'Listings',
  '/intents': 'Intents',
  '/intake-requests': 'Intake Requests',
  '/guest-guide': 'Guest Guide',
  '/users': 'Users',
  '/owners': 'Owners',
  '/tenants': 'Tenants',
  '/roles': 'Roles',
  '/views': 'Views',
  '/catalogs': 'Catalogs',
  '/settings': 'Settings',
  '/profile': 'Profile',
  // Asset & Inventory module
  '/asset-mgr/transactions': 'Inventory Transactions',
  '/asset-mgr/inventory': 'Inventory',
  '/asset-mgr/products': 'Products',
  '/asset-mgr/categories': 'Categories',
  '/asset-mgr/ownerships': 'Ownerships',
  '/asset-mgr/assets': 'Assets',
}

/** 静态映射回退:精确命中 → 最长前缀命中 → 'Nova Vacation' */
function staticRouteTitle(pathname: string): string {
  if (routeTitles[pathname]) return routeTitles[pathname]
  const matched = Object.keys(routeTitles).find(
    (r) => r !== '/' && pathname.startsWith(r)
  )
  return matched ? routeTitles[matched] : 'Nova Vacation'
}

function useRouteTitle() {
  const location = useLocation()
  const locale = useLocale()
  const menuTree = useAppSelector((s) => s.auth.menuTree)
  const groups = useMemo(() => deriveNavGroups(menuTree), [menuTree])
  const activeItem = useMemo(
    () => findActiveItem(groups, location.pathname),
    [groups, location.pathname],
  )

  // 优先:当前路由命中的菜单项 → 取其 labelI18n(随语言切换,与侧栏一致)
  if (activeItem) return pickI18n(activeItem.labelI18n, locale, activeItem.name)
  // 回退:非菜单路由 / 菜单未加载 → 静态英文表
  return staticRouteTitle(location.pathname)
}

/**
 * TopBar 标题区。三种渲染状态:
 * - pageTitle 非 null(详情页):整段替换为 pageTitle
 * - activeViewName 非 null(列表页选中 view):路由标题 › view 名字
 * - 否则:仅路由标题
 */
export function HeaderTitle() {
  const { pageTitle, pageSubtitle, activeViewName } = useAppSelector((s) => s.navigation)
  const routeTitle = useRouteTitle()

  if (pageTitle) {
    // 有副标题时:标题 + 下方小灰字(两行堆叠);无副标题时保持单行
    if (pageSubtitle) {
      return (
        <div className="flex flex-col min-w-0 leading-tight">
          <h1 className="text-lg font-bold text-foreground truncate">{pageTitle}</h1>
          <p className="text-[11px] text-muted-foreground truncate">{pageSubtitle}</p>
        </div>
      )
    }
    return <h1 className="text-lg font-bold text-foreground leading-none truncate">{pageTitle}</h1>
  }

  // 窄屏(手机 390)时顶栏左侧只剩汉堡按钮后的几十 px。路由标题原来只有 shrink-0,
  // 宽度没有上限,"Statements" 这类标题会溢出左栏、压在右侧全局搜索放大镜上。
  // - 路由标题:仍 shrink-0(面包屑永远先让位),但 max-w-full + truncate,
  //   独占都放不下时才出省略号,不再越界。
  // - 「› view 名」整段包一层 min-w-0 overflow-hidden,只拿标题剩下的空间;
  //   与标题的间距放在包裹层里面(chevron 的 ml-2),收起时连间距一起收掉,
  //   不会为了留一个空 gap 把标题多截掉几 px。
  return (
    <h1 className="flex items-center text-lg leading-none min-w-0">
      <span className="max-w-full shrink-0 truncate font-bold text-foreground">{routeTitle}</span>
      {activeViewName && (
        <span className="flex min-w-0 items-center gap-2 overflow-hidden">
          <ChevronRight className="ml-2 h-4 w-4 text-muted-foreground/60 shrink-0" />
          <span className="font-medium text-muted-foreground truncate">{activeViewName}</span>
        </span>
      )}
    </h1>
  )
}
